import Cache from "./cache.js";

export const WS_DATATABLE_INDEX = "__ws_datatable_index";
export const WS_DATATABLE_SELECT = "__ws_datatable_select";
export const WS_DATATABLE_GROUP = "ws_datatable_group";
export const WS_IGNORE_SELECT = "WS_IGNORE_SELECT";

import Filters from "./filters.js";

export const createColumnObject = ({
	name,
	selector = null,
	sortable = true,
	filter = true,
	type = "text",
	tooltip = null
}) => {
	if (type === "text" && !name) {
		throw new Error("Column should have a name");
	}
	if (!["text", "checkbox"].includes(type)) {
		throw new Error("Column should be either a text or checkbox");
	}
	if (type === "checkbox") {
		filter = false;
		sortable = false;
		selector = `ws_datatable_checkbox_column`;
	}
	return {
		name,
		selector: selector || name,
		sortable,
		filter,
		type, // text | checkbox
		tooltip
	};
};

/**
 * Generates a random id (string)
 *
 * @param {Number} length Length of generated ID
 * @return {string}
 */
export const generateRandomId = (length = 10) => {
	let result = "";
	const characters =
		"ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_";
	const charactersLength = characters.length;
	for (let i = 0; i < length; i++) {
		result += characters.charAt(
			Math.floor(Math.random() * charactersLength)
		);
	}
	return result;
};

// Used when unmounting data-table component
export function clearCacheRows() {
	Cache.clearCacheRows();
}

/**
 * Prepare rows for data table.
 * When this function is called, it should return rows in the same order as the currentRows:
 *	              example 1  |   example 2
 *	         rows [1, 2, 3]  |  [2, 1]
 *	 currentRows [2, 1, 3]   |  [1, 3, 2]
 *	      output [2, 1, 3]   |  [1, 2]
 * @param {array} rows Array with rows to be displayed
 * @param {array} currentRows Array with current rows being displayed
 * @returns {object} {rows: array, filters: array}
 */
export const prepareRowsForDataTable = (rows, currentRows, columns, sort) => {
	function getUniqueIdFromRow(row, keys) {
		let uniqueKey = "" + row[keys[0]];
		for (let keyIdx = 1; keyIdx < keys.length; keyIdx++) {
			uniqueKey += row[keys[keyIdx]];
		}
		return uniqueKey;
	}

	function createRow(row, index, keys) {
		const newRow = Object.assign(
			{},
			{
				[WS_DATATABLE_INDEX]: index,
				[WS_DATATABLE_SELECT]: row[WS_DATATABLE_SELECT] || false
			},
			row
		);

		const uniqueId = getUniqueIdFromRow(newRow, keys);
		Cache.addRowToCache(uniqueId, index);
		return newRow;
	}

	// if it's a new data-table it won't do nothing;
	// if values were updated, it's needed to recreate cache because it might have changed order of elements (if sorting elements is removed from this function, it's possible to improve a lot the efficiency of this entiry function)
	Cache.clearCacheRows();

	if (!rows || rows?.length === 0) {
		return {
			rows,
			filters: []
		};
	}

	// if columns were set, use it; otherwise, generate columns from rows' selector field
	const _columns =
		columns?.length > 0
			? columns
			: Object.keys(rows[0]).map((columnName) => ({
					selector: columnName
				}));
	const keys = _columns.flatMap((col) => col.selector);

	const filters = new Filters(columns);
	filters.addFilter("_group", WS_DATATABLE_GROUP);

	// if currentRows is empty/undefined, means ws-data-table has just been initialized (also means rows weren't updated, so simply return rows)
	if (!currentRows) {
		const finalRows = [];
		for (let rowIdx = 0; rowIdx < rows.length; rowIdx++) {
			const row = rows[rowIdx];
			finalRows.push(createRow(row, rowIdx, keys));
			filters.createFilterItemsFromRow(row);
		}
		return {
			rows: finalRows,
			filters: filters.finalFilters
		};
	}

	// if sort is applied, sort new rows to match currentRows
	if (sort && sort.column) {
		rows.sort(
			(rowA, rowB) =>
				naturalSorting(rowA[sort.column], rowB[sort.column]) *
				sort.order
		);
	}

	// when length of new rows is different from currentRows, re-sort rows
	if (rows.length !== currentRows.length) {
		const finalRows = [];
		for (let rowIdx = 0; rowIdx < rows.length; rowIdx++) {
			const row = rows[rowIdx];
			finalRows.push(createRow(row, rowIdx, keys));
			filters.createFilterItemsFromRow(row);
		}
		return {
			rows: finalRows,
			filters: filters.finalFilters
		};
	}

	const finalRows = new Array(rows.length);
	const differentRows = [];
	for (let currRowIdx = 0; currRowIdx < currentRows.length; currRowIdx++) {
		const uniqueIdRow = getUniqueIdFromRow(currentRows[currRowIdx], keys);
		Cache.addRowToCache(uniqueIdRow, currRowIdx); // element position in the array
	}

	const tempCache = {};
	const usedRowIndexes = [];

	// basically we need this loop to keep in the same sorting the elements displayed in data-table (if sorting elements is removed from this function, it's possible to improve a lot the efficiency of this entiry function)
	for (const row of rows) {
		// basically the uniqueId is the combination of the keys from each row. So, if a row changes any of the values from one of the keys, it won't find that uniqueId in the new rows and this one will need to be added to finalRows later.
		const uniqueIdRow = getUniqueIdFromRow(row, keys); // uniqueId from "new rows"
		/**
		 * Because of the way it checks if it's a new element or not in the array, it's needed to save all the indexes in order to avoid having a case where the value of the key is the same for 2 elements and it returns the same index.
		 * Example:
		 *  keys: ["name"]
		 *  rows: [{id: 1, name: "Murilo"}, {id: 2, name: "Murilo"}]
		 * When checking for the name, it will output index 0 (for murilo 1) even when the element in loop is murilo 2.
		 * And as the uniqueId for those 2 elements will be "murilo", that's why the getIndexesFromCache returns always an array (and then uses the usedRowIndexes to make sure it's using the correct element)
		 */
		const currentRowIndexes = Cache.getIndexesFromCache(uniqueIdRow); // find the uniqueId inside "old rows"
		let currentRowIndex = currentRowIndexes[0];
		// in a scenario where rows are [{name: "Murilo"}, {name: "Murilo"}],
		if (currentRowIndexes.length > 1) {
			if (usedRowIndexes.includes(currentRowIndex)) {
				for (
					let iRowIndex = 1;
					iRowIndex < currentRowIndexes.length;
					iRowIndex++
				) {
					currentRowIndex = currentRowIndexes[iRowIndex];
					if (!usedRowIndexes.includes(currentRowIndex)) {
						break; // exit loop
					}
				}
			}
		}

		// if didn't find correspondent element in currentRow, add to differentRows to add it later to empty slots in finalRows
		if (currentRowIndex === -1) {
			differentRows.push(row);
			continue;
		}

		tempCache[uniqueIdRow] = currentRowIndex;
		finalRows[currentRowIndex] = createRow(row, currentRowIndex, keys);
		usedRowIndexes.push(currentRowIndex);
		filters.createFilterItemsFromRow(row);
	}
	Cache.replaceCache(tempCache);

	// when a row is changed, it won't find its uniqueId so it will be needed to add it back to the finalRows - this element will be added to differentRows array (it's needed to keep the sorting order from currentRows). This loop will find all empty slots in finalRows and add all differentRows back to final array
	for (let rowIdx = 0; rowIdx < finalRows.length; rowIdx++) {
		if (!finalRows[rowIdx]) {
			const diffRow = differentRows.shift();
			finalRows[rowIdx] = createRow(diffRow, rowIdx, keys);
			filters.createFilterItemsFromRow(diffRow);
		}

		// exit for-loop when there's no more differentRows to be added
		if (differentRows.length === 0) {
			break;
		}
	}

	return {
		rows: finalRows,
		filters: filters.finalFilters
	};
};

/**
 * Natural Sorting (http://www.davekoelle.com/files/alphanum.js)
 * This sort will solve the issue with string + number ([a2, a1, a10].sort() => [a1, a10, a2] || [a2, a1, a10].naturalSort() => [a1, a2, a10] )
 * @param {array} input
 * @returns {array} Sorted array
 */
export function naturalSorting(a, b) {
	/* alphanum.js (C) Brian Huisman
	 * Based on the Alphanum Algorithm by David Koelle
	 * The Alphanum Algorithm is discussed at http://www.DaveKoelle.com
	 *
	 * Distributed under same license as original
	 *
	 * Released under the MIT License - https://opensource.org/licenses/MIT
	 *
	 * Permission is hereby granted, free of charge, to any person obtaining
	 * a copy of this software and associated documentation files (the "Software"),
	 * to deal in the Software without restriction, including without limitation
	 * the rights to use, copy, modify, merge, publish, distribute, sublicense,
	 * and/or sell copies of the Software, and to permit persons to whom the
	 * Software is furnished to do so, subject to the following conditions:
	 *
	 * The above copyright notice and this permission notice shall be included
	 * in all copies or substantial portions of the Software.
	 *
	 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
	 * EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
	 * MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT.
	 * IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM,
	 * DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR
	 * OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE
	 * USE OR OTHER DEALINGS IN THE SOFTWARE.
	 */
	function chunkify(t) {
		const tz = new Array();
		let x = 0,
			y = -1,
			n = 0,
			i,
			j;

		while ((i = (j = t.charAt(x++)).charCodeAt(0))) {
			const m = i == 46 || (i >= 48 && i <= 57);
			if (m !== n) {
				tz[++y] = "";
				n = m;
			}
			tz[y] += j;
		}
		return tz;
	}

	const aa = chunkify((a || "").toString().toLowerCase());
	const bb = chunkify((b || "").toString().toLowerCase());

	for (let x = 0; aa[x] && bb[x]; x++) {
		if (aa[x] !== bb[x]) {
			const c = Number(aa[x]),
				d = Number(bb[x]);
			if (c == aa[x] && d == bb[x]) {
				return c - d;
			} else {
				return aa[x] > bb[x] ? 1 : -1;
			}
		}
	}
	return aa.length - bb.length;
}

export function createCollapsedGroupsStorageName(tableId = null) {
	let finalName = "collapsedGroup_";
	const locationPathname = window.location.pathname;
	const foundBoxHubsInLocation = /^\/(boxes|hubs)\/v1\//gi.exec(
		locationPathname
	);
	if (foundBoxHubsInLocation) {
		const locationWithouBoxV1 = locationPathname.substring(
			foundBoxHubsInLocation[0].length
		);
		finalName += locationWithouBoxV1;
	} else {
		finalName += locationPathname;
	}

	if (tableId) {
		finalName += `_${tableId}`;
	}

	return finalName.replace(/\//g, "_");
}
